/**
 * Bidirectional URL <-> Zustand state synchronization using the History API.
 *
 * URL structure (Phase 3.5 — aggregations routed to Portfolios page):
 *   /                                → backtesting page (default)
 *   /chat                            → backtesting page (redirected for backward compat)
 *
 * Common routes:
 *   /paper-trading                   → paper-trading page, no specific session
 *   /paper-trading/:sessionId        → paper-trading with specific session selected
 *   /backtesting                     → backtesting page, no run loaded
 *   /backtesting/:runId              → backtesting page with specific run loaded
 *   /strategies                      → configurations page, strategies tab
 *   /strategies/:strategyId          → strategy detail view
 *   /strategies/:strategyId/code     → strategy code viewer
 *   /strategies/:strategyId/configs/:configId         → config detail (runs tab)
 *   /strategies/:strategyId/configs/:configId/:tab    → config detail with specific tab
 *   /portfolios                      → portfolios page, no portfolio selected
 *   /portfolios/:aggId               → portfolios page with specific portfolio selected
 *   /aggregations                    → (legacy) redirects to /portfolios
 *   /aggregations/:aggId             → (legacy) redirects to /portfolios/:aggId
 *   /settings                        → settings page (defaults to apiTokens tab)
 *   /settings/api-tokens             → settings page, API Tokens tab
 *   /settings/exchange-keys          → settings page, Exchange Keys tab
 *   /settings/risk-controls          → settings page, Risk Controls tab
 *   /settings/notifications          → settings page, Notifications tab
 *   /optimizations/grid-search       → optimizations page, Grid Search tab
 *   /optimizations/walk-forward      → optimizations page, Walk-Forward tab
 *   /data-cache                      → admin-data (Data Cache) page
 *
 * Query params (modal state — survive refresh):
 *   ?run=strategy|aggregation        → RunBacktestModal open on given tab
 *   ?optimize=setup|history          → OptimizerModal open on given inner tab
 *   ?optimize=1                      → (legacy) OptimizerModal open, defaults to setup tab
 */

import { useEffect, useRef } from 'react';
import { usePaperTradingStore } from '../stores/paperTradingStore';
import { useBacktestStore } from '../stores/backtestStore';
import { useConfigurationStore } from '../stores/configurationStore';
import { useAggregationStore } from '../stores/aggregationStore';
import { usePortfoliosStore } from '../stores/portfoliosStore';
import { useSettingsStore } from '../stores/settingsStore';
import { useRunBacktestModalStore } from '../stores/runBacktestModalStore';
import { useOptimizerModalStore } from '../stores/optimizerModalStore';
import { useOptimizationsPageStore } from '../stores/optimizationsPageStore';
import type { ActivePage } from '../stores/paperTradingStore';
import type { SettingsTab } from '../stores/settingsStore';
import type { OptimizationsTab } from '../stores/optimizationsPageStore';

/** The default landing page. */
const DEFAULT_PAGE: ActivePage = 'backtesting';

// ============================================================================
// Settings tab slug mapping
// Rule 11: slugToSettingsTab MUST throw on unknown slug — no silent fallback.
// ============================================================================

const SLUG_TO_TAB: Record<string, SettingsTab> = {
  'api-tokens': 'apiTokens',
  'exchange-keys': 'exchangeKeys',
  'risk-controls': 'riskControls',
  'notifications': 'notifications',
};

const TAB_TO_SLUG: Record<SettingsTab, string> = {
  apiTokens: 'api-tokens',
  exchangeKeys: 'exchange-keys',
  riskControls: 'risk-controls',
  notifications: 'notifications',
};

/** Convert a kebab-case URL slug to a SettingsTab.
 * Throws on unknown slug — Rule 11 (no silent fallback). */
export function slugToSettingsTab(slug: string): SettingsTab {
  const tab = SLUG_TO_TAB[slug];
  if (!tab) throw new Error(`Unknown settings tab slug: "${slug}"`);
  return tab;
}

/** Convert a SettingsTab to its kebab-case URL slug. */
export function settingsTabToSlug(tab: SettingsTab): string {
  return TAB_TO_SLUG[tab];
}

// ============================================================================
// Optimizations tab slug mapping
// Rule 11: slugToOptimizationsTab MUST throw on unknown slug — no silent fallback.
// ============================================================================

const SLUG_TO_OPTIMIZATIONS_TAB: Record<string, OptimizationsTab> = {
  'grid-search': 'grid_search',
  'walk-forward': 'walk_forward',
};

const OPTIMIZATIONS_TAB_TO_SLUG: Record<OptimizationsTab, string> = {
  grid_search: 'grid-search',
  walk_forward: 'walk-forward',
};

/** Convert a kebab-case URL slug to an OptimizationsTab.
 * Throws on unknown slug — Rule 11 (no silent fallback). */
export function slugToOptimizationsTab(slug: string): OptimizationsTab {
  const tab = SLUG_TO_OPTIMIZATIONS_TAB[slug];
  if (!tab) throw new Error(`Unknown optimizations tab slug: "${slug}"`);
  return tab;
}

/** Convert an OptimizationsTab to its kebab-case URL slug. */
export function optimizationsTabToSlug(tab: OptimizationsTab): string {
  return OPTIMIZATIONS_TAB_TO_SLUG[tab];
}

// ============================================================================
// Optimizer modal inner tab values
// Rule 11: parseOptimizerParam MUST throw on unknown value — no silent fallback.
// ============================================================================

type OptimizerModalTab = 'setup' | 'history';

/** Parse the ?optimize= query param value into an OptimizerModalTab.
 * Accepts 'setup', 'history', and the legacy '1' (treated as 'setup').
 * Throws on any other value — Rule 11 (no silent fallback). */
function parseOptimizerParam(value: string): OptimizerModalTab {
  if (value === 'setup' || value === '1') return 'setup';
  if (value === 'history') return 'history';
  throw new Error(`Unknown optimizer modal tab value: "${value}"`);
}

// ============================================================================
// Helpers
// ============================================================================

interface ParsedPath {
  page: ActivePage;
  resourceId: string | null;
  configTab: 'strategies' | 'aggregations' | null;
  aggregationId: string | null;
  strategyId: string | null;
  configId: string | null;
  codeView: boolean;
  detailTab: string | null;
  /** Portfolio id from /portfolios/:id or legacy /aggregations/:id; null means list view. */
  portfolioId: string | null;
  /** Settings sub-tab from /settings/:tab; null means use store default (apiTokens). */
  settingsTab: SettingsTab | null;
  /** Optimizations page sub-tab from /optimizations/:tab; null means use store default (grid_search). */
  optimizationsTab: OptimizationsTab | null;
  /** Optimization run id from /optimizations/:tab/:id; null means list view. */
  optimizationId: string | null;
  /** Whether the run backtest modal should be open (?run=1). */
  runModalOpen: boolean;
  /** Whether the optimizer modal should be open (?optimize=setup|history). */
  optimizerOpen: boolean;
  /** Optimizer modal inner tab from ?optimize=; null when modal closed. */
  optimizerModalTab: OptimizerModalTab | null;
}

/**
 * Parse a pathname (and optional search string) into structured route state.
 *
 * @param pathname - e.g. "/settings/exchange-keys"
 * @param search   - e.g. "?run=strategy&optimize=1" (defaults to window.location.search)
 *
 * Throws if the settings tab slug is unknown (Rule 11).
 */
export function parsePathname(pathname: string, search?: string): ParsedPath {
  const segments = pathname.split('/').filter(Boolean);

  // Parse query params
  const searchStr = search !== undefined ? search : (typeof window !== 'undefined' ? window.location.search : '');
  const params = new URLSearchParams(searchStr);
  const rawRun = params.get('run');
  const runModalOpen = rawRun !== null;
  const rawOptimize = params.get('optimize');
  // parseOptimizerParam throws on unknown values — Rule 11 (no silent fallback).
  const optimizerOpen = rawOptimize !== null;
  const optimizerModalTab: OptimizerModalTab | null =
    rawOptimize !== null ? parseOptimizerParam(rawOptimize) : null;

  const baseResult = (page: ActivePage, extra?: Partial<ParsedPath>): ParsedPath => ({
    page,
    resourceId: null,
    configTab: null,
    aggregationId: null,
    strategyId: null,
    configId: null,
    codeView: false,
    detailTab: null,
    portfolioId: null,
    settingsTab: null,
    optimizationsTab: null,
    optimizationId: null,
    runModalOpen,
    optimizerOpen,
    optimizerModalTab,
    ...extra,
  });

  if (segments[0] === 'backtesting') {
    return baseResult('backtesting', { resourceId: segments[1] ?? null });
  }

  if (segments[0] === 'paper-trading') {
    return baseResult('paper-trading', { resourceId: segments[1] ?? null });
  }

  if (segments[0] === 'settings') {
    const slug = segments[1] ?? null;
    // slugToSettingsTab throws on unknown slug — Rule 11
    const settingsTab: SettingsTab | null = slug ? slugToSettingsTab(slug) : null;
    return baseResult('settings', { settingsTab });
  }

  if (segments[0] === 'optimizations') {
    const slug = segments[1] ?? null;
    // slugToOptimizationsTab throws on unknown slug — Rule 11
    const optimizationsTab: OptimizationsTab | null = slug ? slugToOptimizationsTab(slug) : null;
    // segments[2] is a free-form id (no slug mapping — plain string, URL-decoded).
    const optimizationId: string | null = segments[2] ? decodeURIComponent(segments[2]) : null;
    return baseResult('optimizations', { optimizationsTab, optimizationId });
  }

  if (segments[0] === 'data-cache') {
    return baseResult('admin-data');
  }

  if (segments[0] === 'admin' && segments[1] === 'activity') {
    return baseResult('admin-activity');
  }

  if (segments[0] === 'terms') {
    return baseResult('terms');
  }

  if (segments[0] === 'privacy') {
    return baseResult('privacy');
  }

  if (segments[0] === 'strategies') {
    const strategyId = segments[1] ?? null;
    let configId: string | null = null;
    let codeView = false;
    let detailTab: string | null = null;

    if (segments[1] && segments[2] === 'code') {
      codeView = true;
    } else if (segments[1] && segments[2] === 'configs' && segments[3]) {
      configId = segments[3];
      detailTab = segments[4] ?? null;
    }

    return baseResult('configurations', {
      configTab: 'strategies',
      strategyId,
      configId,
      codeView,
      detailTab,
    });
  }

  // Legacy: /aggregations and /aggregations/:id redirect to the Portfolios page.
  if (segments[0] === 'aggregations') {
    return baseResult('portfolios', {
      portfolioId: segments[1] ?? null,
    });
  }

  // Legacy: /configs/strategy/:id and /configs/aggregation/:id
  if (segments[0] === 'configs') {
    if (segments[1] === 'strategy') {
      return baseResult('configurations', {
        resourceId: segments[2] ?? null,
        configTab: 'strategies',
        configId: segments[2] ?? null,
      });
    }
    if (segments[1] === 'aggregation') {
      // Legacy /configs/aggregation/:id → Portfolios page.
      return baseResult('portfolios', {
        portfolioId: segments[2] ?? null,
      });
    }
    return baseResult('configurations', { configTab: 'strategies' });
  }

  if (segments[0] === 'portfolios') {
    return baseResult('portfolios', {
      portfolioId: segments[1] ?? null,
    });
  }

  if (segments[0] === 'chat') {
    return baseResult(DEFAULT_PAGE);
  }

  // Default: / → DEFAULT_PAGE (chat when enabled, backtesting otherwise)
  return baseResult(DEFAULT_PAGE);
}

/**
 * Build the pathname portion of the URL from current app state.
 *
 * @param settingsTab        - current settings tab, or null to omit the sub-segment.
 * @param optimizationsTab   - current optimizations tab, or null to use the default (grid-search).
 * @param optimizationId     - currently open optimization run id, or null/undefined for list view.
 */
export function buildPathname(
  page: ActivePage,
  sessionId: string | null,
  backtestId: string | null,
  configId: string | null,
  aggregationId: string | null,
  activeConfigTab: 'strategies' | 'aggregations',
  strategyId: string | null,
  codeViewOpen: boolean,
  activeDetailTab: string,
  settingsTab: SettingsTab | null,
  optimizationsTab?: OptimizationsTab | null,
  optimizationId?: string | null,
  portfolioId?: string | null,
): string {
  if (page === 'paper-trading') {
    if (sessionId) return `/paper-trading/${sessionId}`;
    return '/paper-trading';
  }

  if (page === 'settings') {
    if (settingsTab) return `/settings/${settingsTabToSlug(settingsTab)}`;
    return '/settings';
  }

  if (page === 'optimizations') {
    // Default to grid-search when no tab is specified.
    const slug = optimizationsTab ? optimizationsTabToSlug(optimizationsTab) : 'grid-search';
    if (optimizationId) {
      return `/optimizations/${slug}/${encodeURIComponent(optimizationId)}`;
    }
    return `/optimizations/${slug}`;
  }

  if (page === 'portfolios') {
    if (portfolioId) return `/portfolios/${portfolioId}`;
    return '/portfolios';
  }

  if (page === 'admin-data') {
    return '/data-cache';
  }

  if (page === 'admin-activity') {
    return '/admin/activity';
  }

  if (page === 'terms') {
    return '/terms';
  }

  if (page === 'privacy') {
    return '/privacy';
  }

  if (page === 'configurations') {
    if (activeConfigTab === 'aggregations') {
      if (aggregationId) return `/aggregations/${aggregationId}`;
      return '/aggregations';
    }
    // strategies tab
    if (strategyId && configId) {
      if (activeDetailTab && activeDetailTab !== 'runs') {
        return `/strategies/${strategyId}/configs/${configId}/${activeDetailTab}`;
      }
      return `/strategies/${strategyId}/configs/${configId}`;
    }
    if (strategyId && codeViewOpen) {
      return `/strategies/${strategyId}/code`;
    }
    if (strategyId) {
      return `/strategies/${strategyId}`;
    }
    return '/strategies';
  }

  if (backtestId) return `/backtesting/${backtestId}`;
  return '/backtesting';
}

/**
 * Build the query string from modal state.
 * Returns an empty string when no modals are open.
 *
 * The ?optimize= param carries both open state and inner tab:
 *   ?optimize=setup    → modal open on Setup tab
 *   ?optimize=history  → modal open on History tab
 */
export function buildSearch(
  runModalOpen: boolean,
  optimizerOpen: boolean,
  optimizerModalTab: OptimizerModalTab | null,
): string {
  const p = new URLSearchParams();
  if (runModalOpen) p.set('run', '1');
  if (optimizerOpen) {
    // Default to 'setup' when no tab is specified.
    p.set('optimize', optimizerModalTab ?? 'setup');
  }
  const str = p.toString();
  return str ? `?${str}` : '';
}

// ============================================================================
// Router guard (SHA-23)
// ============================================================================

/** A parse result that never throws — used by the router/consumer (useUrlSync). */
interface SafeParseResult {
  parsed: ParsedPath;
  /**
   * The raw pathname that failed to parse, or null when parsing succeeded.
   * Surfaced so the consumer (OptimizationsPage) can render a graceful
   * not-found panel instead of the whole app white-screening.
   */
  notFoundPath: string | null;
}

function emptyParsedPath(page: ActivePage): ParsedPath {
  return {
    page,
    resourceId: null,
    configTab: null,
    aggregationId: null,
    strategyId: null,
    configId: null,
    codeView: false,
    detailTab: null,
    portfolioId: null,
    settingsTab: null,
    optimizationsTab: null,
    optimizationId: null,
    runModalOpen: false,
    optimizerOpen: false,
    optimizerModalTab: null,
  };
}

/**
 * Wraps the pure (fail-loud) `parsePathname` so the router/consumer never
 * throws on an unknown/missing slug. `parsePathname` itself stays fail-loud
 * per Rule 11 — this guard only lives at the consumer boundary (this hook),
 * exactly like a route-level error boundary.
 *
 * On failure, falls back to a safe, renderable page state and reports the
 * offending pathname via `notFoundPath` so the page-level component can show
 * a 404/not-found panel instead of crashing the whole app.
 */
export function safeParsePathname(pathname: string, search?: string): SafeParseResult {
  try {
    return { parsed: parsePathname(pathname, search), notFoundPath: null };
  } catch (err) {
    console.error(`[useUrlSync] Failed to parse route "${pathname}" — rendering not-found fallback.`, err);
    const segments = pathname.split('/').filter(Boolean);
    // Best-effort fallback: keep the recognizable page prefix (currently the
    // only route family known to hit this — /optimizations/<bare-id>) so the
    // not-found panel renders in-context; otherwise fall back to the default page.
    const page: ActivePage = segments[0] === 'optimizations' ? 'optimizations' : DEFAULT_PAGE;
    return { parsed: emptyParsedPath(page), notFoundPath: pathname };
  }
}

// ============================================================================
// Hook
// ============================================================================

export function useUrlSync(): void {
  const setActivePage = usePaperTradingStore((s) => s.setActivePage);
  const setSelectedSession = usePaperTradingStore((s) => s.setSelectedSession);
  const setSelectedBacktestId = useBacktestStore((s) => s.setSelectedBacktestId);
  const setSelectedConfigId = useConfigurationStore((s) => s.setSelectedConfigId);
  const setSelectedAggregation = useAggregationStore((s) => s.setSelectedAggregation);

  // Ref to avoid pushing a history entry while we are already handling a
  // popstate event (back/forward navigation).
  const isFromPopState = useRef(false);

  // Ref to track the last URL we pushed so we skip redundant pushState calls
  // (especially important on initial mount where state + URL are already in sync).
  const lastPushedUrl = useRef<string | null>(null);

  // -------------------------------------------------------------------------
  // Shared: restore modal state from parsed URL
  // -------------------------------------------------------------------------
  function restoreModalsFromParsed(parsed: ParsedPath) {
    // Run backtest modal
    if (parsed.runModalOpen) {
      useRunBacktestModalStore.setState({ isOpen: true });
    } else {
      useRunBacktestModalStore.setState({ isOpen: false });
    }

    // Optimizer modal — carry both open state and inner tab
    if (parsed.optimizerOpen && parsed.optimizerModalTab) {
      useOptimizerModalStore.setState({
        isOptimizerModalOpen: true,
        optimizerModalTab: parsed.optimizerModalTab,
      });
    } else {
      useOptimizerModalStore.setState({ isOptimizerModalOpen: false });
    }
  }

  // -------------------------------------------------------------------------
  // 1. On mount: URL → State
  // -------------------------------------------------------------------------
  useEffect(() => {
    const { parsed, notFoundPath } = safeParsePathname(window.location.pathname, window.location.search);

    // Apply page
    setActivePage(parsed.page);

    // Apply resource id
    if (parsed.page === 'paper-trading') {
      setSelectedSession(parsed.resourceId);
    } else if (parsed.page === 'settings') {
      if (parsed.settingsTab) {
        useSettingsStore.setState({ activeTab: parsed.settingsTab });
      }
      // Clear cross-page selections
      setSelectedBacktestId(null);
      setSelectedSession(null);
      setSelectedConfigId(null);
      setSelectedAggregation(null);
      useConfigurationStore.setState({ selectedStrategyId: null, codeViewOpen: false });
    } else if (parsed.page === 'configurations') {
      // Set the config tab
      if (parsed.configTab) {
        useConfigurationStore.setState({ activeConfigTab: parsed.configTab });
      }
      // Set strategy-level state
      if (parsed.strategyId) {
        useConfigurationStore.setState({ selectedStrategyId: parsed.strategyId });
      }
      if (parsed.codeView) {
        useConfigurationStore.setState({ codeViewOpen: true });
      }
      if (parsed.configId) {
        setSelectedConfigId(parsed.configId);
      }
      if (parsed.detailTab) {
        const validTabs = ['runs', 'paper', 'versions'] as const;
        type DetailTab = typeof validTabs[number];
        if (validTabs.includes(parsed.detailTab as DetailTab)) {
          useConfigurationStore.setState({ activeDetailTab: parsed.detailTab as DetailTab });
        }
      }
      setSelectedAggregation(parsed.aggregationId);
    } else if (parsed.page === 'optimizations') {
      if (parsed.optimizationsTab) {
        useOptimizationsPageStore.setState({ activeTab: parsed.optimizationsTab });
      }
      // Hydrate the detail selection from the URL (may be null → list view).
      useOptimizationsPageStore.setState({ selectedOptimizationId: parsed.optimizationId });
      // Clear cross-page selections.
      setSelectedBacktestId(null);
      setSelectedSession(null);
      setSelectedConfigId(null);
      setSelectedAggregation(null);
      useConfigurationStore.setState({ selectedStrategyId: null, codeViewOpen: false });
    } else if (parsed.page === 'portfolios') {
      // Set the selected portfolio from the URL (null → list view).
      usePortfoliosStore.setState({ selectedPortfolioId: parsed.portfolioId });
      // Clear cross-page selections.
      setSelectedBacktestId(null);
      setSelectedSession(null);
      setSelectedConfigId(null);
      setSelectedAggregation(null);
      useConfigurationStore.setState({ selectedStrategyId: null, codeViewOpen: false });
    } else if (parsed.page === 'admin-data' || parsed.page === 'admin-activity' || parsed.page === 'terms' || parsed.page === 'privacy') {
      // These pages have no associated resource id — clear all cross-page selections.
      setSelectedBacktestId(null);
      setSelectedSession(null);
      setSelectedConfigId(null);
      setSelectedAggregation(null);
      useConfigurationStore.setState({ selectedStrategyId: null, codeViewOpen: false });
    } else {
      setSelectedBacktestId(parsed.resourceId);
    }

    // SHA-23: surface (or clear) the route-parse failure for the not-found panel.
    useOptimizationsPageStore.setState({ routeNotFoundPath: notFoundPath });

    // Restore modal states from query params
    restoreModalsFromParsed(parsed);

    // Record the current URL as already-synced
    const { activeConfigTab, selectedStrategyId, codeViewOpen, activeDetailTab } =
      useConfigurationStore.getState();
    const { activeTab: settingsTab } = useSettingsStore.getState();
    const { activeTab: optimizationsTab, selectedOptimizationId } = useOptimizationsPageStore.getState();
    const { selectedPortfolioId: portfolioId } = usePortfoliosStore.getState();
    const canonicalPath = buildPathname(
      parsed.page,
      parsed.page === 'paper-trading' ? parsed.resourceId : null,
      parsed.page === 'backtesting' ? parsed.resourceId : null,
      parsed.configId,
      parsed.aggregationId,
      activeConfigTab,
      selectedStrategyId,
      codeViewOpen,
      activeDetailTab,
      parsed.page === 'settings' ? settingsTab : null,
      parsed.page === 'optimizations' ? optimizationsTab : null,
      parsed.page === 'optimizations' ? selectedOptimizationId : null,
      parsed.page === 'portfolios' ? portfolioId : null,
    );
    const { isOpen: runOpen } = useRunBacktestModalStore.getState();
    const { isOptimizerModalOpen, optimizerModalTab } = useOptimizerModalStore.getState();
    const canonicalSearch = buildSearch(runOpen, isOptimizerModalOpen, isOptimizerModalOpen ? optimizerModalTab : null);
    const canonicalUrl = canonicalPath + canonicalSearch;
    lastPushedUrl.current = canonicalUrl;

    // Normalise the URL in place
    const currentUrl = window.location.pathname + window.location.search;
    if (currentUrl !== canonicalUrl) {
      window.history.replaceState(null, '', canonicalUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------------------------------------------------------------------------
  // 2. On state change: State → URL
  // -------------------------------------------------------------------------
  useEffect(() => {
    function computeAndPush() {
      if (isFromPopState.current) return;

      const { activePage, selectedSessionId } = usePaperTradingStore.getState();
      const { selectedBacktestId } = useBacktestStore.getState();
      const {
        selectedConfigId,
        activeConfigTab,
        selectedStrategyId,
        codeViewOpen,
        activeDetailTab,
      } = useConfigurationStore.getState();
      const { selectedAggregationId } = useAggregationStore.getState();
      const { selectedPortfolioId } = usePortfoliosStore.getState();
      const { activeTab: settingsTab } = useSettingsStore.getState();
      const { activeTab: optimizationsTab, selectedOptimizationId } = useOptimizationsPageStore.getState();
      const { isOpen: runOpen } = useRunBacktestModalStore.getState();
      const { isOptimizerModalOpen, optimizerModalTab } = useOptimizerModalStore.getState();

      const newPath = buildPathname(
        activePage,
        selectedSessionId,
        selectedBacktestId,
        selectedConfigId,
        selectedAggregationId,
        activeConfigTab,
        selectedStrategyId,
        codeViewOpen,
        activeDetailTab,
        activePage === 'settings' ? settingsTab : null,
        activePage === 'optimizations' ? optimizationsTab : null,
        activePage === 'optimizations' ? selectedOptimizationId : null,
        activePage === 'portfolios' ? selectedPortfolioId : null,
      );
      const newSearch = buildSearch(runOpen, isOptimizerModalOpen, isOptimizerModalOpen ? optimizerModalTab : null);
      const newUrl = newPath + newSearch;

      if (newUrl !== lastPushedUrl.current) {
        lastPushedUrl.current = newUrl;
        window.history.pushState(null, '', newUrl);
      }
    }

    // Subscribe to changes in all relevant stores.
    const unsubPaperTrading = usePaperTradingStore.subscribe(computeAndPush);
    const unsubBacktest = useBacktestStore.subscribe(computeAndPush);
    const unsubConfig = useConfigurationStore.subscribe(computeAndPush);
    const unsubAggregation = useAggregationStore.subscribe(computeAndPush);
    const unsubPortfolios = usePortfoliosStore.subscribe(computeAndPush);
    const unsubSettings = useSettingsStore.subscribe(computeAndPush);
    const unsubOptimizationsPage = useOptimizationsPageStore.subscribe(computeAndPush);
    const unsubRunModal = useRunBacktestModalStore.subscribe(computeAndPush);
    const unsubOptimizer = useOptimizerModalStore.subscribe(computeAndPush);

    return () => {
      unsubPaperTrading();
      unsubBacktest();
      unsubConfig();
      unsubAggregation();
      unsubPortfolios();
      unsubSettings();
      unsubOptimizationsPage();
      unsubRunModal();
      unsubOptimizer();
    };
  }, []);

  // -------------------------------------------------------------------------
  // 3. On browser back/forward: popstate → State
  // -------------------------------------------------------------------------
  useEffect(() => {
    function handlePopState() {
      isFromPopState.current = true;

      const { parsed, notFoundPath } = safeParsePathname(window.location.pathname, window.location.search);

      setActivePage(parsed.page);

      if (parsed.page === 'paper-trading') {
        setSelectedSession(parsed.resourceId);
        setSelectedBacktestId(null);
        setSelectedConfigId(null);
        setSelectedAggregation(null);
        useConfigurationStore.setState({
          selectedStrategyId: null,
          codeViewOpen: false,
        });
      } else if (parsed.page === 'settings') {
        if (parsed.settingsTab) {
          useSettingsStore.setState({ activeTab: parsed.settingsTab });
        }
        setSelectedBacktestId(null);
        setSelectedSession(null);
        setSelectedConfigId(null);
        setSelectedAggregation(null);
        useConfigurationStore.setState({ selectedStrategyId: null, codeViewOpen: false });
      } else if (parsed.page === 'configurations') {
        if (parsed.configTab) {
          useConfigurationStore.setState({ activeConfigTab: parsed.configTab });
        }
        useConfigurationStore.setState({
          selectedStrategyId: parsed.strategyId,
          codeViewOpen: parsed.codeView,
        });
        setSelectedConfigId(parsed.configId);
        if (parsed.detailTab) {
          const validTabs = ['runs', 'paper', 'versions'] as const;
          type DetailTab = typeof validTabs[number];
          if (validTabs.includes(parsed.detailTab as DetailTab)) {
            useConfigurationStore.setState({ activeDetailTab: parsed.detailTab as DetailTab });
          }
        }
        setSelectedAggregation(parsed.aggregationId);
        setSelectedSession(null);
        setSelectedBacktestId(null);
      } else if (parsed.page === 'optimizations') {
        if (parsed.optimizationsTab) {
          useOptimizationsPageStore.setState({ activeTab: parsed.optimizationsTab });
        }
        // Restore detail selection from popped URL (null clears to list view).
        useOptimizationsPageStore.setState({ selectedOptimizationId: parsed.optimizationId });
        setSelectedBacktestId(null);
        setSelectedSession(null);
        setSelectedConfigId(null);
        setSelectedAggregation(null);
        useConfigurationStore.setState({ selectedStrategyId: null, codeViewOpen: false });
      } else if (parsed.page === 'portfolios') {
        // Restore the selected portfolio from the popped URL (null clears to list view).
        usePortfoliosStore.setState({ selectedPortfolioId: parsed.portfolioId });
        setSelectedBacktestId(null);
        setSelectedSession(null);
        setSelectedConfigId(null);
        setSelectedAggregation(null);
        useConfigurationStore.setState({ selectedStrategyId: null, codeViewOpen: false });
      } else if (parsed.page === 'admin-data' || parsed.page === 'admin-activity' || parsed.page === 'terms' || parsed.page === 'privacy') {
        // These pages have no associated resource id — clear all cross-page selections.
        setSelectedBacktestId(null);
        setSelectedSession(null);
        setSelectedConfigId(null);
        setSelectedAggregation(null);
        useConfigurationStore.setState({ selectedStrategyId: null, codeViewOpen: false });
      } else {
        setSelectedBacktestId(parsed.resourceId);
        setSelectedSession(null);
        setSelectedConfigId(null);
        setSelectedAggregation(null);
        useConfigurationStore.setState({
          selectedStrategyId: null,
          codeViewOpen: false,
        });
      }

      // SHA-23: surface (or clear) the route-parse failure for the not-found panel.
      useOptimizationsPageStore.setState({ routeNotFoundPath: notFoundPath });

      // Restore modal state from the popped URL
      restoreModalsFromParsed(parsed);

      lastPushedUrl.current = window.location.pathname + window.location.search;

      setTimeout(() => {
        isFromPopState.current = false;
      }, 0);
    }

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [setActivePage, setSelectedBacktestId, setSelectedSession, setSelectedConfigId, setSelectedAggregation]);
}
